import { useToast } from '@ais-ui/ui-core';
import React, { useCallback, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEditGroundingConfiguration, useShapeFetch } from '@ais-ui/api';
import { useGetFilters, useGetShareGroups } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { GroundingDetectorDialog } from './GroundingDetectorDialog';
import {
  convertGroundingConfigurationFormValuesToBackend,
  groundingDetectorFormSchema,
  GroundingDetectorFormValues,
} from './groundingForm';
import { GroundingConfigurationSchema } from '@ais-ui/api-spec/grounding';
import { grabEventDetectorInitialGroups } from '../../../Groups/grabInitialGroups';
import { mutateCallbacks } from '../../../utils/mutateCallbacks';

interface EditDetectorDialogProps {
  detector: GroundingConfigurationSchema;
  showDialog: boolean;
  setShowDialog: (showDialog: boolean) => void;
}

export const EditGroundingDetectorDialog = ({
  detector,
  showDialog,
  setShowDialog,
}: EditDetectorDialogProps) => {
  const { messages } = useLocalization();
  const { mutate } = useEditGroundingConfiguration();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formInitialized, setFormInitialized] = React.useState(false);
  const { toast } = useToast();
  const shapeFetch = useShapeFetch();

  const { data: filters } = useGetFilters();
  const { data: groups, isLoading: isLoadingGroups } = useGetShareGroups();

  const form = useForm<GroundingDetectorFormValues>({
    resolver: zodResolver(groundingDetectorFormSchema),
    defaultValues: {
      name: detector.name,
      enabled: detector.enabled as boolean,
      shapeId: detector.shape as string,
      shape: detector.geometry,
      selectedGroups: grabEventDetectorInitialGroups(detector, groups),
      severity: detector.severity as string,
      useShallowWaters: detector.parameters?.useShallowWaters,
      useSogDecrease: detector.parameters?.useSogDecrease,
      filterPorts: detector.parameters?.filterPorts,
      filterAnchorageAreas: detector.parameters?.filterAnchorageAreas,
      useRemainsGroundedCheck: detector.parameters?.useRemainsGroundedCheck,
      validateMinutes: detector.parameters?.validateMinutes,
      // Just setting defaults here for now until it is implemented in the backend, just not to break anything.
      filterId: 'no-filter',
      acknowledgeRequired: false,
      maintainShiplist: false,
      emails: detector.emailAddresses,
      phones: detector.phoneNumbers,
    },
  });

  useEffect(() => {
    if (!isLoadingGroups && !formInitialized) {
      form.reset({
        ...form.getValues(), // Preserve other values
        selectedGroups: grabEventDetectorInitialGroups(detector, groups),
      });
      setFormInitialized(true); // Prevent re-initialization
    }
  }, [isLoadingGroups, groups, form, formInitialized, detector]);

  const onEdit = useCallback(
    async (data: GroundingDetectorFormValues) => {
      if (!filters) {
        return;
      }

      let shape = null;
      if (data.shapeId) {
        shape = await shapeFetch(data.shapeId);
      }

      if (!shape) {
        return;
      }

      const filter = filters.find((filter) => filter.uuid === data.filterId);

      const backendValues = convertGroundingConfigurationFormValuesToBackend(
        data,
        shape,
        filter
      );

      setIsSubmitting(true);

      const callBacks = mutateCallbacks(
        toast,
        messages,
        'edit',
        'detector',
        setIsSubmitting,
        setShowDialog
      );

      mutate(
        {
          id: detector.id as number,
          groundingConfigurationSchema: backendValues,
        },
        callBacks
      );
    },
    [filters, shapeFetch, mutate, detector.id]
  );

  return (
    <GroundingDetectorDialog
      form={form}
      onSubmit={onEdit}
      setShowOuterDialog={setShowDialog}
      mutateType="edit"
      isSubmitting={isSubmitting}
    ></GroundingDetectorDialog>
  );
};
